import { MatLegacyDialogRef as MatDialogRef, MatLegacyDialogConfig as MatDialogConfig, LegacyDialogPosition as DialogPosition } from '@angular/material/legacy-dialog';
import { PopUpManager } from '../../services/core/popup/popup.manager';
import { Subscription, BehaviorSubject, Observable } from 'rxjs';
import { ConfirmationPopRenderer } from './confirmation/confirmation-pop.renderer';
import { ToastRenderer } from './wiz-toast/toast.renderer';
import { ToastManager } from '../../services/core/popup/toast.manager';
import { OverlayRef } from '@angular/cdk/overlay';
import { CONFIRMATION_MODE, WIZ_PHYSIC_POSITION } from '@wizeoo/akm-core-lib';

/**
 * required configuration for a popUp genesis
 * dialogRef is the container
 * classFactory is the factory of the (to be) instance
 */
export class WizPopUp {
  public static confirmDefaultClass: any = ConfirmationPopRenderer; // renderer par défaut pour les popUp de confirmation
  public static toastDefaultClass: any = ToastRenderer; // renderer par défaut pour les popUp de confirmation

  // rendering class (classFactory)
  // must implement IWizPop
  public classFactory: any;
  // Instance of the pop (real, you can touch), feed by popUpManager
  public dialogRef: MatDialogRef<any>; // feed by  PopUpmanager on first show
  // configuration stuff
  public dialogConfig: MatDialogConfig; // feed on first build

  // UUID feed by constructor
  public uuid: string;
  // flag : this pop is already complete
  protected isBuild: boolean = false;

  /** pile and lock */
  public modal: boolean; // modal pop, or not (= dialogConfig.disableClose) : background access disallowed
  public locked: boolean; // locked pop (won't close on mouseDownOutside) but background clic is allowed.
  public mouseDownOutsideCloseSubscriber: Subscription; // global listener set by Manager for nonModal closure attempt
  // Stronger than modal. (modal = false)

  public deep: number; // parent's deep, 1 if dialogRef is aview, else parent's deep + 1 (pop of the pop)

  /** physics */
  public position: number; // coordinates enum, WIZ_PHYSIC_POSITION
  public width: string; // dialogConfig.width
  public height: string; // dialogConfig.height

  /** styles */
  public panelClass: string; // Css class to use on classFactory
  public customPosition: DialogPosition; // position isn't enough ? do it yourself

  public datas: any; // source datas
  public callback: Function; // méthode de retour

  public title: string; // en mode "confirmation", le titre basic (si pas de classFactory)
  public contentText: string; // en mode "confirmation", le texte (si pas de classFactory)
  public confirmMode: CONFIRMATION_MODE; // les boutons à afficher pour la pop de confirmation

  public customFactory: any; // renderer custom pour la pop de confirmation / toast
  public customFactoryData: any; // les données éventuelles à utiliser dans le renderer

  public icon: any; // la source de l'éventuelle icone

  public showCloseButton: boolean; //  affichage du bouton de fermeture (toast)
  public tapToClose: boolean; //  fermeture sur click (toast)
  public timeToLive: number; // durée de vie en ms (toast)

  public isToast: boolean = false; // c'est une instance de toast (overlay différent)
  public toastOverlay: OverlayRef; // la couche container pour le toast

  protected _onHide$: BehaviorSubject<any> = new BehaviorSubject(null);

  public get onHide$(): Observable<any> {
    return this._onHide$.asObservable();
  }

  /** construction
   * @param classFactory rendering class
   * @param modal modal pop, or not (= dialogConfig.disableClose)
   * @param locked locked pop (won't close on mouseDownOutside), stronger than modal
   * @param position coordinates enum, WIZ_PHYSIC_POSITION
   * @param width dialogConfig.width
   * @param height dialogConfig.height
   * @param datas source datas
   * @param panelClass Css class to use on classFactory
   * @param customPosition position isn't enough ? do it yourself
   */
  constructor(
    classFactory: any,
    modal: boolean = false,
    locked: boolean = false,
    position: number = WIZ_PHYSIC_POSITION.TOP_LEFT,
    width: string = null, // '100px',
    height: string = null, // '100%',
    datas: any = null,
    panelClass: string = null,
    customPosition: DialogPosition = null
  ) {
    // this.uuid = WizMathUtils.createUUID(); // feed on view instanciation

    this.classFactory = classFactory;
    this.modal = modal;
    this.locked = locked;
    if (this.locked) {
      // to be locked, you must first be non-modal
      this.modal = false;
    }
    this.position = position;

    this.width = width;
    if (this.classFactory && this.classFactory.requestWidth) {
      // requestWidth en statique sur la classe basePop
      this.width = this.classFactory.requestWidth;
      // console.log('requestWidth', this.classFactory.requestWidth);
    }

    this.height = height;
    this.datas = datas;
    this.panelClass = panelClass;
    this.customPosition = customPosition;
  }

  /** wanta show the popUp, build it if needed */
  public show(): WizPopUp {
    if (!this.isBuild) {
      this.buildMe();
    }

    // it's the manager' s job
    if (this.isToast) {
      return ToastManager.show(this);
    } else {
      return PopUpManager.show(this);
    }
  }

  /** closure requirement */
  public hide(pResult?: any): WizPopUp {
    this._onHide$.next(pResult);
    this._onHide$.complete();

    if (this.isToast) {
      return ToastManager.hide(this);
    } else {
      return PopUpManager.hide(this);
    }
  }

  /** build configuration objects */
  protected buildMe(): void {
    this.isBuild = true; 
    // dialogConfig genesis

    this.dialogConfig = new MatDialogConfig();

    this.dialogConfig.disableClose = this.modal;
    this.dialogConfig.autoFocus = true;
    this.dialogConfig.width = this.width;
    this.dialogConfig.maxWidth = this.width;
    this.dialogConfig.height = this.height;
    this.dialogConfig.hasBackdrop = !this.locked;
    this.dialogConfig.panelClass = this.panelClass;

    if (this.customPosition) {
      this.dialogConfig.position = this.customPosition;
    } else {
      // set "where"
      switch (this.position) {
        case WIZ_PHYSIC_POSITION.TOP_LEFT:
          this.dialogConfig.position = { top: '0px', left: '0px' };
          break;

        case WIZ_PHYSIC_POSITION.TOP_RIGHT:
          this.dialogConfig.position = { top: '0px', right: '0px' };
          break;

        case WIZ_PHYSIC_POSITION.BOTTOM_LEFT:
          this.dialogConfig.position = { bottom: '0px', left: '0px' };
          break;

        case WIZ_PHYSIC_POSITION.BOTTOM_RIGHT:
          this.dialogConfig.position = { bottom: '0px', right: '0px' };
          break;
        // case MIDDLE_CENTER : do nothing, you are here
      }
    }
    this.dialogConfig.data = this.datas;
  }

  /**
   * Alimentation de la partie "rendu custom" pour les confirms et toasts
   * @param renderer : classe de rendu pour remplacer le détail
   * @param rendererData : les données à passer au renderer (data sinon)
   */
  public setRenderer(renderer: any, rendererData: any = null): WizPopUp {
    this.customFactory = renderer;
    this.customFactoryData = rendererData;
    return this;
  }

  /**
   * Alimentation de la position / taille
   * @param position: number = WIZ_PHYSIC_POSITION.TOP_LEFT
   * @param width ... la largeur
   * @param height ... la hauteur
   */
  public setSizeAndPos(
    position: number = WIZ_PHYSIC_POSITION.TOP_LEFT,
    width: string = null,
    height: string = null
  ): WizPopUp {
    this.position = position;
    this.width = width;
    this.height = height;
    return this;
  }

  /**
   * set callback method (n'effectue pas le bind)
   * @param f callback
   */
  public setCallback(f: Function): WizPopUp {
    this.callback = f;
    return this;
  }

  /**
   * pop de confirmation. Va alimenter une popUp toute faite
   * @param title : le titre (obligatoire)
   * @param contentText : le détail (fortement conseillé si pas de renderer)
   * @param callback va recevoir un objet ConfirmResultObj
   * @param mode : un objet CONFIRMATION_MODE, defaut = YES / NO
   * @param datas: les données à faire transiter / afficher dans le renderer
   * @param renderer : une éventuelle classe de rendu pour remplacer le détail
   * @param rendererData : les données à passer au renderer (data sinon)
   */
  public static confirm(
    title: string,
    contentText: string,
    callback: Function,
    mode: CONFIRMATION_MODE = CONFIRMATION_MODE.YES_NO,
    datas: any = null,
    renderer: any = null,
    rendererData: any = null,
    width?: string,
    height?: string
  ): WizPopUp {
    const result: WizPopUp = new WizPopUp(
      WizPopUp.confirmDefaultClass,
      true,
      false,
      WIZ_PHYSIC_POSITION.MIDDLE_CENTER,
      width ? width : '450px',
      height ? height : '160px',
      datas
    );

    result.title = title;
    result.contentText = contentText;
    result.setCallback(callback);
    result.confirmMode = mode;
    if (renderer) {
      result.customFactory = renderer;
      result.customFactoryData = rendererData;
      // result.height = '200px';// un peu plus haut si un renderer, par défaut (toujours modifiable)
    }

    return result;
  }

  /**
   * toast tout cuit..
   * @param text : le texte
   * @param icon : l'icône
   * @param ttl : timeToLive: durée d'affichage en ms (5s par défaut)
   * @param showCloseButton : affichage de la croix de fermeture
   * @param tapToClose : fermeture sur click
   */
  public static toast(
    text: string,
    icon: any = null,
    ttl: number = ToastManager.defaultTimeOut,
    showCloseButton: boolean = ToastManager.defaultShowCloseButton,
    tapToClose: boolean = ToastManager.defaultTapClose,
    width: string = '270px',
    height: string = '90px'
  ): WizPopUp {
    const result: WizPopUp = new WizPopUp(WizPopUp.toastDefaultClass, false, false, null, width, height); // la position est overridée par le toastManager
    result.contentText = text;
    result.icon = icon;

    result.timeToLive = ttl;
    result.showCloseButton = showCloseButton;
    result.tapToClose = tapToClose;

    result.isToast = true;

    return result;
  }
}
